import { Box, Button, Container, Stack, Typography } from '@mui/material';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AdjustRoundedIcon from '@mui/icons-material/AdjustRounded';

import heroDesktop from '../assets/hero-desktop.jpg';
import heroMobile from '../assets/hero-mobile.jpg';

const features = [
  { icon: <InsightsRoundedIcon fontSize="small" />, label: 'Executive Coaching' },
  { icon: <TrendingUpRoundedIcon fontSize="small" />, label: 'Leadership Development' },
  { icon: <AdjustRoundedIcon fontSize="small" />, label: 'Results Focused' },
] as const;

export function Hero() {
  return (
    <Box
      component="section"
      id="top"
      sx={{
        position: 'relative',
        backgroundColor: 'background.default',
        // Desktop: full-bleed background image — Logan sits on the right,
        // blurred light area on the left absorbs the headline + CTAs.
        // `right top` anchors the photo to the top edge so at very wide
        // viewports the cover-scale overflow falls off the bottom (arms)
        // instead of clipping his head.
        backgroundImage: { md: `url(${heroDesktop})` },
        backgroundSize: { md: 'cover' },
        backgroundPosition: { md: 'right top' },
        backgroundRepeat: 'no-repeat',
        minHeight: { md: 640, lg: 720 },
        pb: { xs: 8, md: 12 },
        pt: { xs: 6, md: 12 },
        // Subtle left-side gradient on desktop to guarantee text legibility
        // even if a future hero photo is darker than this one.
        '&::before': {
          content: { md: '""', xs: 'none' },
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(245, 241, 232, 0.92) 0%, rgba(245, 241, 232, 0.55) 38%, rgba(245, 241, 232, 0) 62%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: { md: 'center' },
          minHeight: { md: 640, lg: 720 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
            alignItems: 'center',
            gap: { xs: 5, md: 7 },
            width: '100%',
          }}
        >
          {/* Text column */}
          <Stack spacing={{ xs: 3, md: 4 }} sx={{ maxWidth: 560 }}>
            <Typography
              variant="overline"
              component="p"
              sx={{ color: 'primary.main', fontSize: { xs: '0.75rem', md: '0.82rem' } }}
            >
              Growth Focus Solutions
            </Typography>

            <Typography
              variant="h1"
              component="h1"
              sx={{
                color: 'primary.dark',
                fontSize: { xs: '2.3rem', sm: '2.7rem', md: '3rem', lg: '3.4rem' },
              }}
            >
              Grow into the leader you aspire to be.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: 500,
              }}
            >
              Increase your capacity. Elevate your thinking. Executive coaching for leaders ready
              to take the next step &mdash; your growth starts with a free connection call.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
              <Button variant="contained" color="primary" size="large" href="#contact">
                Let&rsquo;s grow
              </Button>
              <Button variant="outlined" color="primary" size="large" href="#approach">
                What is coaching?
              </Button>
            </Stack>

            <Stack
              direction="row"
              spacing={{ xs: 2.5, md: 4 }}
              sx={{
                pt: { xs: 2, md: 3 },
                color: 'primary.main',
                flexWrap: 'wrap',
                rowGap: 2,
              }}
            >
              {features.map((f) => (
                <Stack
                  key={f.label}
                  direction="row"
                  spacing={1}
                  sx={{ color: 'primary.main', alignItems: 'center' }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: { xs: '0.78rem', md: '0.82rem' },
                      lineHeight: 1.2,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {f.label.replace(' ', '\n')}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          {/* Mobile-only image column. Desktop uses the section's background image. */}
          <Box
            sx={{
              position: 'relative',
              justifySelf: { xs: 'center', md: 'end' },
              width: '100%',
              maxWidth: { xs: 360 },
              display: { xs: 'block', md: 'none' },
            }}
          >
            <Box
              component="picture"
              sx={{
                display: 'block',
                width: '100%',
                '& img': {
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: 1,
                },
              }}
            >
              <img
                src={heroMobile}
                alt="Logan White, Executive Coach for Growth Focus Solutions"
                width={800}
                height={1421}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Curved cream wave divider */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -1,
          left: 0,
          right: 0,
          lineHeight: 0,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" width="100%" height="80">
          <path
            d="M0,40 C240,80 480,0 720,32 C960,64 1200,48 1440,16 L1440,80 L0,80 Z"
            fill="#ebe5d6"
            opacity="0.7"
          />
        </svg>
      </Box>
    </Box>
  );
}
